import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { ServiceCard } from "./ServiceCard";
import { err } from "./StepForm";

const box = "rounded-xl border border-slate-200 bg-white p-3 text-[12px]";
const b = "rounded-full border border-slate-300 px-3 py-1 font-semibold";
const CL = { NOT_YET_CONFIRMED: "Not yet confirmed", PARTIAL: "Partly confirmed", CONFIRMED: "Confirmed by customer" };

function Emails({ rows, reload }) {
  const retry = (id) => api.post(`/service-confirmations/emails/${id}/retry`).then((r) => { toast.success(r.data.status); reload(); }).catch((e) => toast.error(err(e)));
  if (!rows?.length) return null;
  return (
    <div className="space-y-0.5 text-[11px]" data-testid="svc-emails">
      {rows.map((e) => <div key={e.id} data-testid={`svc-email-${e.id}`}>{e.created_at?.slice(0, 16).replace("T", " ")} · {e.kind.replace(/_/g, " ").toLowerCase()} · <b>{e.status}</b>{e.delivery ? ` · ${e.delivery}` : ""}{e.reason ? ` · ${e.reason}` : ""}{e.recipients?.to?.length ? ` → ${e.recipients.to.join(", ")}` : ""}
        {["FAILED", "BLOCKED", "HELD", "UNKNOWN"].includes(e.status) && <button className="ml-2 underline" onClick={() => retry(e.id)} data-testid={`svc-email-retry-${e.id}`}>Retry</button>}</div>)}
    </div>
  );
}

export function ServicesSection({ oid }) {
  const [st, setSt] = useState(null);
  const [html, setHtml] = useState("");
  const load = useCallback(() => api.get(`/service-confirmations/orders/${oid}`).then((r) => setSt(r.data)).catch(() => setSt(null)), [oid]);
  useEffect(() => { load(); }, [load]);
  if (!st?.services) return null;
  const call = (p, msg) => api.post(`/service-confirmations/orders/${oid}/${p}`).then((r) => { toast.success(msg || r.data.status || "Done"); load(); }).catch((e) => toast.error(err(e)));
  const preview = () => api.get(`/service-confirmations/orders/${oid}/report/preview`).then((r) => setHtml(r.data.html)).catch((e) => toast.error(err(e)));
  const send = () => api.post(`/service-confirmations/orders/${oid}/report/send`, { request_id: (crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`) }).then((r) => { toast.success(r.data.duplicate ? "Already sent" : "Report queued"); load(); }).catch((e) => toast.error(err(e)));
  const svcs = (st.service_order || Object.keys(st.services)).map((k) => st.services[k]);
  return (
    <section className={box} data-testid="services-section">
      <div className="mb-1 flex flex-wrap items-center gap-2 text-sm font-bold">Services confirmation
        <span className="rounded-full bg-slate-100 px-2 text-[11px]" data-testid="svc-checklist-state">{CL[st.checklist_state]}</span>
        <span className="text-[11px] font-normal text-slate-500" data-testid="svc-switch">Emails {st.switch?.enabled ? "ON" : "OFF"}</span>
      </div>
      {st.virtual ? (
        <div className="space-y-1.5">
          <div className="text-slate-600">No services checklist yet for this order. Nothing is sent automatically for orders accepted before activation.</div>
          <div className="flex flex-wrap gap-1.5">{svcs.map((s) => <span key={s.key} className="rounded-full bg-amber-50 px-2 text-[11px] text-amber-900">{s.label}: Not yet confirmed{s.quoted ? " (in quotation)" : ""}</span>)}</div>
          <button className={b} onClick={() => call("open", "Checklist opened")} data-testid="svc-open">Open services checklist</button>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="text-slate-600" data-testid="svc-request-state">Customer request: <b>{(st.request?.state || "NOT_SENT").replace(/_/g, " ")}</b>{st.request?.reason ? ` · ${st.request.reason}` : ""} · Recipients: <b>{st.recipients?.source === "MISSING" ? "Missing — add customer logistics contacts in Customer dispatch update" : [...(st.recipients?.to || []), ...(st.recipients?.cc || [])].join(", ")}</b>
            {st.link && <> · link expires {st.link.expires_at?.slice(0, 10)}</>}</div>
          <div className="flex flex-wrap gap-1.5">
            <button className={b} onClick={() => call("request", "Request queued")} data-testid="svc-send-request">{st.request?.state === "NOT_SENT" ? "Send details request" : "Resend request (new link)"}</button>
            {st.link && <button className={b} onClick={() => call("revoke-link", "Link revoked")} data-testid="svc-revoke-link">Revoke link</button>}
            <button className={b} onClick={preview} data-testid="svc-report-preview">Preview report</button>
            <button className={b} onClick={send} data-testid="svc-report-send">Send report</button>
          </div>
          {svcs.map((s) => <ServiceCard key={s.key} oid={oid} svc={s} reload={load} />)}
          <Emails rows={st.emails} reload={load} />
          {html && <iframe title="Services report preview" srcDoc={html} sandbox="" className="h-[520px] w-full rounded border border-slate-200" data-testid="svc-report-frame" />}
        </div>
      )}
    </section>
  );
}
